<template>
  <div class="interface-management">
    <!-- 工具栏 -->
    <div class="toolbar" v-fade-in>
      <div class="toolbar-left">
        <h3>接口链接管理</h3>
        <span class="stats">共 {{ apiLinks.length }} 个接口</span>
      </div>
      <div class="toolbar-right">
        <button class="btn btn-domain" @click="openDomainModal">
          <Globe :size="16" />
          <span>导入域名</span>
        </button>
        <button class="btn btn-secondary" @click="openBatchModal">
          <Plus :size="16" />
          <span>批量导入</span>
        </button>
        <button class="btn btn-primary" @click="openAddModal">
          <Plus :size="16" />
          <span>添加接口</span>
        </button>
      </div>
    </div>

    <!-- 批量操作栏 -->
    <div class="batch-bar" v-if="selectedIds.size > 0" v-fade-in>
      <div class="batch-info">
        <span class="batch-count">已选 <strong>{{ selectedIds.size }}</strong> 个接口</span>
        <button class="batch-link" @click="clearSelection">取消选择</button>
      </div>
      <div class="batch-actions">
        <button class="btn btn-batch-refund" @click="batchRefund">
          <RotateCcw :size="14" />
          <span>批量退款</span>
        </button>
        <button class="btn btn-batch-delete" @click="batchDelete">
          <Trash2 :size="14" />
          <span>批量删除</span>
        </button>
      </div>
    </div>

    <!-- 接口列表 -->
    <div class="table-container" v-if="apiLinks.length > 0" v-motion="{ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4, delay: 0.05 } }">
      <table class="data-table">
        <thead>
          <tr>
            <th style="width: 40px">
              <input type="checkbox" :checked="isAllSelected" @change="toggleSelectAll" class="row-checkbox" />
            </th>
            <th style="width: 60px">序号</th>
            <th style="width: 150px">名称</th>
            <th>接口链接</th>
            <th style="width: 100px">金额</th>
            <th style="width: 100px" v-if="isAdmin">创建者</th>
            <th style="width: 160px">添加时间</th>
            <th style="width: 160px">操作</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(item, index) in groupedApiLinks" :key="item.type === 'group' ? item.id : item.link.id">
            <!-- 批量导入分组 -->
            <template v-if="item.type === 'group'">
              <tr class="group-row" @click="toggleGroup(item.id)">
                <td class="text-center" @click.stop>
                  <input type="checkbox" :checked="isGroupSelected(item)" @change="toggleSelectGroup(item)" class="row-checkbox" />
                </td>
                <td class="text-center">{{ index + 1 }}</td>
                <td>
                  <div class="group-name-cell">
                    <span class="group-arrow">{{ expandedGroups[item.id] ? '▾' : '▸' }}</span>
                    <span>{{ item.prefix }}</span>
                    <span class="group-badge">{{ item.count }}个</span>
                  </div>
                </td>
                <td class="text-muted" style="font-size: 12px;">批量导入 · 点击展开</td>
                <td>
                  <span v-if="item.isFree" class="free-tag">免费</span>
                  <span v-else class="price-text">¥{{ item.price || 0 }}</span>
                </td>
                <td v-if="isAdmin">
                  <span class="creator-tag">{{ item.createdBy || 'admin' }}</span>
                </td>
                <td class="text-muted">{{ formatDate(item.createdAt) }}</td>
                <td>
                  <div class="action-group">
                    <button class="action-btn delete" @click.stop="deleteGroup(item.links)" title="批量删除">
                      <Trash2 :size="14" />
                    </button>
                  </div>
                </td>
              </tr>
              <template v-if="expandedGroups[item.id]">
                <tr v-for="(link, subIdx) in item.links" :key="link.id" class="group-child-row">
                  <td class="text-center" @click.stop>
                    <input type="checkbox" :checked="selectedIds.has(link.id)" @change="toggleSelect(link.id)" class="row-checkbox" />
                  </td>
                  <td class="text-center text-muted" style="font-size: 11px;">{{ subIdx + 1 }}</td>
                  <td>
                    <template v-if="editingId === link.id">
                      <input v-model="editForm.name" class="edit-input" placeholder="接口名称" />
                    </template>
                    <template v-else>{{ link.name || '-' }}</template>
                  </td>
                  <td>
                    <template v-if="editingId === link.id">
                      <input v-model="editForm.url" class="edit-input" placeholder="接口链接" />
                    </template>
                    <template v-else>
                      <div class="url-cell">
                        <span class="url-text">{{ link.url }}</span>
                        <button class="icon-btn" @click="copyUrl(link.url)" title="复制链接">
                          <Copy :size="14" />
                        </button>
                      </div>
                    </template>
                  </td>
                  <td>
                    <template v-if="editingId === link.id">
                      <input v-if="!editForm.isFree" v-model="editForm.price" class="edit-input" type="number" step="0.01" placeholder="金额" style="width: 80px" />
                      <span v-else class="free-tag">免费</span>
                    </template>
                    <template v-else>
                      <span v-if="link.isFree" class="free-tag">免费</span>
                      <span v-else class="price-text">¥{{ link.price || 0 }}</span>
                    </template>
                  </td>
                  <td v-if="isAdmin">
                    <span class="creator-tag">{{ link.createdBy || 'admin' }}</span>
                  </td>
                  <td class="text-muted">{{ formatDate(link.createdAt) }}</td>
                  <td>
                    <div class="action-group">
                      <template v-if="editingId === link.id">
                        <button class="action-btn save" @click="saveEdit(link.id)">
                          <Check :size="14" /> 保存
                        </button>
                        <button class="action-btn cancel" @click="cancelEdit">
                          <X :size="14" />
                        </button>
                      </template>
                      <template v-else>
                        <button class="action-btn edit" @click="startEdit(link)">
                          <Pencil :size="14" />
                        </button>
                        <button class="action-btn refund" @click.stop="openRefundModal(link)" title="退款">
                          <RotateCcw :size="14" />
                        </button>
                        <button class="action-btn delete" @click="deleteLink(link.id)">
                          <Trash2 :size="14" />
                        </button>
                      </template>
                    </div>
                  </td>
                </tr>
              </template>
            </template>
            <!-- 单个接口 -->
            <tr v-else>
              <td class="text-center">
                <input type="checkbox" :checked="selectedIds.has(item.link.id)" @change="toggleSelect(item.link.id)" class="row-checkbox" />
              </td>
              <td class="text-center">{{ index + 1 }}</td>
              <td>
                <template v-if="editingId === item.link.id">
                  <input v-model="editForm.name" class="edit-input" placeholder="接口名称" />
                </template>
                <template v-else>
                  {{ item.link.name || '-' }}
                </template>
              </td>
              <td>
                <template v-if="editingId === item.link.id">
                  <input v-model="editForm.url" class="edit-input" placeholder="接口链接" />
                </template>
                <template v-else>
                  <div class="url-cell">
                    <span class="url-text">{{ item.link.url }}</span>
                    <button class="icon-btn" @click="copyUrl(item.link.url)" title="复制链接">
                      <Copy :size="14" />
                    </button>
                  </div>
                </template>
              </td>
              <td>
                <template v-if="editingId === item.link.id">
                  <input v-if="!editForm.isFree" v-model="editForm.price" class="edit-input" type="number" step="0.01" placeholder="金额" style="width: 80px" />
                  <span v-else class="free-tag">免费</span>
                </template>
                <template v-else>
                  <span v-if="item.link.isFree" class="free-tag">免费</span>
                  <span v-else class="price-text">¥{{ item.link.price || 0 }}</span>
                  <span class="limit-tag" :class="{ unlimited: item.link.maxPurchases === 0 }">{{ item.link.maxPurchases === 0 ? '不限' : '独享' }}</span>
                </template>
              </td>
              <td v-if="isAdmin">
                <span class="creator-tag">{{ item.link.createdBy || 'admin' }}</span>
              </td>
              <td class="text-muted">{{ formatDate(item.link.createdAt) }}</td>
              <td>
                <div class="action-group">
                  <template v-if="editingId === item.link.id">
                    <button class="action-btn save" @click="saveEdit(item.link.id)">
                      <Check :size="14" />
                      保存
                    </button>
                    <button class="action-btn cancel" @click="cancelEdit">
                      <X :size="14" />
                    </button>
                  </template>
                  <template v-else>
                    <button class="action-btn edit" @click="startEdit(item.link)">
                      <Pencil :size="14" />
                    </button>
                    <button class="action-btn refund" @click="openRefundModal(item.link)" title="退款">
                      <RotateCcw :size="14" />
                    </button>
                    <button class="action-btn delete" @click="deleteLink(item.link.id)">
                      <Trash2 :size="14" />
                    </button>
                  </template>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- 移动端卡片列表 -->
    <div class="card-list" v-if="apiLinks.length > 0" v-motion="{ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4, delay: 0.05 } }">
      <template v-for="(item, index) in groupedApiLinks" :key="item.type === 'group' ? ('card-' + item.id) : ('card-' + item.link.id)">
        <!-- 分组卡片 -->
        <div v-if="item.type === 'group'" class="api-card group-card" :class="{ 'card-selected': isGroupSelected(item) }">
          <div class="card-header" @click="toggleGroup(item.id)" style="cursor: pointer;">
            <span class="card-index" style="display: flex; align-items: center; gap: 8px;">
              <input type="checkbox" :checked="isGroupSelected(item)" @change="toggleSelectGroup(item)" @click.stop class="row-checkbox" />
              <span class="group-arrow">{{ expandedGroups[item.id] ? '▾' : '▸' }}</span>
              {{ item.prefix }}
              <span class="group-badge">{{ item.count }}个</span>
            </span>
            <span class="card-price">
              <span v-if="item.isFree" class="free-tag">免费</span>
              <template v-else>¥{{ item.price || 0 }}</template>
            </span>
          </div>
          <div class="card-body" v-if="!expandedGroups[item.id]">
            <div class="card-name text-muted" style="font-size: 12px;">批量导入 · 点击展开</div>
          </div>
          <template v-if="expandedGroups[item.id]">
            <div v-for="link in item.links" :key="link.id" class="group-child-card">
              <div class="card-body" v-if="editingId !== link.id">
                <div class="card-name">{{ link.name || '未命名接口' }}</div>
                <div class="card-url">{{ link.url }}</div>
              </div>
              <div class="card-body card-edit-form" v-else>
                <input v-model="editForm.name" class="card-input" placeholder="接口名称" />
                <input v-model="editForm.url" class="card-input" placeholder="接口链接" />
                <input v-if="!editForm.isFree" v-model="editForm.price" class="card-input" type="number" step="0.01" placeholder="金额" />
                <span v-else class="free-tag">免费</span>
              </div>
              <div class="card-actions">
                <template v-if="editingId === link.id">
                  <button class="card-btn save" @click="saveEdit(link.id)">
                    <Check :size="16" /> 保存
                  </button>
                  <button class="card-btn cancel" @click="cancelEdit">
                    <X :size="16" /> 取消
                  </button>
                </template>
                <template v-else>
                  <button class="card-btn copy" @click="copyUrl(link.url)">
                    <Copy :size="16" /> 复制
                  </button>
                  <button class="card-btn edit" @click="startEdit(link)">
                    <Pencil :size="16" /> 编辑
                  </button>
                  <button class="card-btn refund" @click.stop="openRefundModal(link)">
                    <RotateCcw :size="16" /> 退款
                  </button>
                  <button class="card-btn delete" @click="deleteLink(link.id)">
                    <Trash2 :size="16" /> 删除
                  </button>
                </template>
              </div>
            </div>
          </template>
          <div class="card-actions" v-if="!expandedGroups[item.id]">
            <button class="card-btn" @click="toggleGroup(item.id)">展开查看</button>
            <button class="card-btn delete" @click="deleteGroup(item.links)">
              <Trash2 :size="16" /> 批量删除
            </button>
          </div>
        </div>
        <!-- 单个接口卡片 -->
        <div v-else class="api-card" :class="{ 'card-selected': selectedIds.has(item.link.id) }">
          <div class="card-header">
            <span class="card-index" style="display: flex; align-items: center; gap: 8px;">
              <input type="checkbox" :checked="selectedIds.has(item.link.id)" @change="toggleSelect(item.link.id)" class="row-checkbox" />
              #{{ index + 1 }}
            </span>
            <span class="card-price">
              <span v-if="item.link.isFree" class="free-tag">免费</span>
              <template v-else>¥{{ item.link.price || 0 }}</template>
            </span>
          </div>
          <div class="card-body" v-if="editingId !== item.link.id">
            <div class="card-name">{{ item.link.name || '未命名接口' }}</div>
            <div class="card-url">{{ item.link.url }}</div>
            <div class="card-meta">
              <span class="card-time">{{ formatDate(item.link.createdAt) }}</span>
              <span class="creator-tag" v-if="isAdmin">{{ item.link.createdBy || 'admin' }}</span>
            </div>
          </div>
          <div class="card-body card-edit-form" v-else>
            <input v-model="editForm.name" class="card-input" placeholder="接口名称" />
            <input v-model="editForm.url" class="card-input" placeholder="接口链接" />
            <input v-if="!editForm.isFree" v-model="editForm.price" class="card-input" type="number" step="0.01" placeholder="金额" />
            <span v-else class="free-tag">免费</span>
          </div>
          <div class="card-actions">
            <template v-if="editingId === item.link.id">
              <button class="card-btn save" @click="saveEdit(item.link.id)">
                <Check :size="16" /> 保存
              </button>
              <button class="card-btn cancel" @click="cancelEdit">
                <X :size="16" /> 取消
              </button>
            </template>
            <template v-else>
              <button class="card-btn copy" @click="copyUrl(item.link.url)">
                <Copy :size="16" /> 复制
              </button>
              <button class="card-btn edit" @click="startEdit(item.link)">
                <Pencil :size="16" /> 编辑
              </button>
              <button class="card-btn refund" @click="openRefundModal(item.link)">
                <RotateCcw :size="16" /> 退款
              </button>
              <button class="card-btn delete" @click="deleteLink(item.link.id)">
                <Trash2 :size="16" /> 删除
              </button>
            </template>
          </div>
        </div>
      </template>
    </div>

    <!-- 空状态 -->
    <div class="empty-state" v-else v-motion="{ initial: { opacity: 0, scale: 0.95 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 0.3 } }">
      <Link2 :size="64" class="empty-icon" />
      <p>暂无接口链接</p>
      <button class="btn btn-primary" @click="openAddModal">
        <Plus :size="16" />
        <span>添加第一个接口</span>
      </button>
    </div>

    <!-- 添加弹窗 -->
    <div class="modal-overlay" v-if="showAddModal" @click="closeAddModal">
      <div class="modal-box" @click.stop>
        <div class="modal-header">
          <h4>添加接口链接</h4>
          <button class="modal-close" @click="closeAddModal">
            <X :size="18" />
          </button>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label>接口链接 <span class="required">*</span></label>
            <input 
              v-model="addForm.url" 
              class="form-input"
              placeholder="请输入接口链接，如 https://api.example.com/v1"
            />
          </div>
          <div class="form-item">
            <label>接口名称</label>
            <input
              v-model="addForm.name"
              class="form-input"
              placeholder="可选，便于识别"
            />
          </div>
          <div class="form-item">
            <label>接口类型</label>
            <div class="type-toggle">
              <button class="type-btn" :class="{ active: !addForm.isFree }" @click="addForm.isFree = false">付费</button>
              <button class="type-btn free" :class="{ active: addForm.isFree }" @click="addForm.isFree = true; addForm.price = 0">免费</button>
            </div>
          </div>
          <div class="form-item" v-if="!addForm.isFree">
            <label>接口单价 <span class="required">*</span></label>
            <input
              v-model.number="addForm.price"
              class="form-input"
              type="number"
              min="0.01"
              step="0.01"
              placeholder="请输入价格，如 1.00"
            />
          </div>
          <div class="form-item">
            <label>购买人数</label>
            <div class="type-toggle">
              <button class="type-btn" :class="{ active: addForm.maxPurchases === 1 }" @click="addForm.maxPurchases = 1">1人独享</button>
              <button class="type-btn unlimited" :class="{ active: addForm.maxPurchases === 0 }" @click="addForm.maxPurchases = 0">无限购买</button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeAddModal">取消</button>
          <button class="btn btn-primary" @click="addLink">确定添加</button>
        </div>
      </div>
    </div>

    <!-- 批量导入弹窗 -->
    <div class="modal-overlay" v-if="showBatchModal" @click="closeBatchModal">
      <div class="modal-box" @click.stop>
        <div class="modal-header">
          <h4>批量导入接口</h4>
          <button class="modal-close" @click="closeBatchModal">
            <X :size="18" />
          </button>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label>接口链接 <span class="required">*</span></label>
            <textarea
              v-model="batchForm.urls"
              class="form-input"
              style="height: 160px; resize: vertical; font-family: monospace; font-size: 12px;"
              placeholder="一行一个接口链接，例如：&#10;https://api1.example.com&#10;https://api2.example.com&#10;https://api3.example.com"
            ></textarea>
          </div>
          <div class="form-item">
            <label>名称前缀</label>
            <input
              v-model="batchForm.name"
              class="form-input"
              placeholder="可选，如填 防红 则自动命名为 防红1、防红2..."
            />
          </div>
          <div class="form-item">
            <label>接口类型</label>
            <div class="type-toggle">
              <button class="type-btn" :class="{ active: !batchForm.isFree }" @click="batchForm.isFree = false">付费</button>
              <button class="type-btn free" :class="{ active: batchForm.isFree }" @click="batchForm.isFree = true; batchForm.price = 0">免费</button>
            </div>
          </div>
          <div class="form-item" v-if="!batchForm.isFree">
            <label>接口单价 <span class="required">*</span></label>
            <input
              v-model.number="batchForm.price"
              class="form-input"
              type="number"
              min="0.01"
              step="0.01"
              placeholder="请输入价格，如 1.00"
            />
          </div>
          <div class="form-item">
            <label>购买人数</label>
            <div class="type-toggle">
              <button class="type-btn" :class="{ active: batchForm.maxPurchases === 1 }" @click="batchForm.maxPurchases = 1">1人独享</button>
              <button class="type-btn unlimited" :class="{ active: batchForm.maxPurchases === 0 }" @click="batchForm.maxPurchases = 0">无限购买</button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeBatchModal">取消</button>
          <button class="btn btn-primary" @click="batchImport">确定导入</button>
        </div>
      </div>
    </div>

    <!-- 导入域名弹窗 -->
    <div class="modal-overlay" v-if="showDomainModal" @click="closeDomainModal">
      <div class="modal-box domain-modal-box" @click.stop>
        <div class="modal-header">
          <h4>导入域名</h4>
          <button class="modal-close" @click="closeDomainModal">
            <X :size="18" />
          </button>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label>域名列表 <span class="required">*</span></label>
            <textarea
              v-model="domainForm.domains"
              class="form-input"
              style="height: 120px; resize: vertical; font-family: monospace; font-size: 12px;"
              placeholder="一行一个域名，例如：&#10;baicai.com&#10;example.cn&#10;abc.xyz"
              @input="generateDomainPreview"
            ></textarea>
          </div>
          <div class="form-item">
            <label>名称前缀</label>
            <input
              v-model="domainForm.name"
              class="form-input"
              placeholder="可选，如填 防红 则自动命名为 防红1、防红2..."
            />
          </div>
          <div class="form-row">
            <div class="form-item" style="flex:1">
              <label>接口类型</label>
              <div class="type-toggle">
                <button class="type-btn" :class="{ active: !domainForm.isFree }" @click="domainForm.isFree = false">付费</button>
                <button class="type-btn free" :class="{ active: domainForm.isFree }" @click="domainForm.isFree = true; domainForm.price = 0">免费</button>
              </div>
            </div>
            <div class="form-item" style="flex:1" v-if="!domainForm.isFree">
              <label>接口单价 <span class="required">*</span></label>
              <input
                v-model.number="domainForm.price"
                class="form-input"
                type="number"
                min="0.01"
                step="0.01"
                placeholder="如 1.00"
              />
            </div>
          </div>
          <!-- 预览区 -->
          <div class="form-item" v-if="domainPreviews.length > 0">
            <label>预览 <span style="color:#999;font-weight:400">（共 {{ domainPreviews.length }} 条）</span></label>
            <div class="domain-preview-list">
              <div v-for="(item, idx) in domainPreviews" :key="idx" class="domain-preview-item">
                <div class="domain-preview-info">
                  <div class="domain-preview-name">{{ item.name }}</div>
                  <div class="domain-preview-url">{{ item.url }}</div>
                </div>
                <div class="domain-preview-qr">
                  <img v-if="item.qrDataUrl" :src="item.qrDataUrl" width="64" height="64" alt="二维码" />
                  <div v-else class="qr-loading">生成中...</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeDomainModal">取消</button>
          <button class="btn btn-primary" @click="domainImport" :disabled="domainPreviews.length === 0">确定导入</button>
        </div>
      </div>
    </div>

    <!-- 退款弹窗 -->
    <div class="modal-overlay" v-if="showRefundModal" @click="closeRefundModal">
      <div class="modal-box" @click.stop>
        <div class="modal-header">
          <h4>退款 - {{ refundTarget ? refundTarget.name || '未命名接口' : '' }}</h4>
          <button class="modal-close" @click="closeRefundModal">
            <X :size="18" />
          </button>
        </div>
        <div class="modal-body">
          <div v-if="isLoadingRefund" style="text-align: center; color: #999; padding: 20px;">
            加载购买记录...
          </div>
          <div v-else-if="refundPurchases.length === 0" style="text-align: center; color: #999; padding: 20px;">
            该接口暂无购买记录
          </div>
          <div v-else>
            <div style="margin-bottom: 12px; font-size: 13px; color: #666;">
              共 <strong>{{ refundPurchases.length }}</strong> 位用户购买了此接口，退款将退还所有用户的购买金额并删除购买记录。
            </div>
            <div class="refund-user-list">
              <div v-for="item in refundPurchases" :key="item.id" class="refund-user-item">
                <span class="refund-username">{{ item.username }}</span>
                <span class="refund-price" v-if="item.isFree">免费</span>
                <span class="refund-price" v-else>¥{{ item.price || 0 }}</span>
                <span class="refund-date">{{ formatRefundDate(item.purchasedAt) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeRefundModal">取消</button>
          <button class="btn btn-refund" @click="confirmRefund" :disabled="refundPurchases.length === 0">
            <RotateCcw :size="14" />
            确认退款
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from '@/config/api'
import { Plus, Copy, Pencil, Trash2, Check, X, Link2, RotateCcw, Globe } from 'lucide-vue-next'
import QRCode from 'qrcode'

export default {
  name: 'InterfaceManagement',
  components: {
    Plus,
    Copy,
    Pencil,
    Trash2,
    Check,
    X,
    Link2,
    RotateCcw,
    Globe
  },
  data() {
    return {
      apiLinks: [],
      selectedIds: new Set(),
      showAddModal: false,
      editingId: null,
      showRefundModal: false,
      refundTarget: null,
      refundPurchases: [],
      isLoadingRefund: false,
      isAdmin: false,
      expandedGroups: {},
      addForm: {
        url: '',
        name: '',
        price: 0,
        isFree: false,
        maxPurchases: 1
      },
      editForm: {
        url: '',
        name: '',
        price: 0,
        isFree: false,
        maxPurchases: 1
      },
      showBatchModal: false,
      batchForm: {
        urls: '',
        name: '',
        price: 0,
        isFree: false,
        maxPurchases: 1
      },
      // 导入域名
      showDomainModal: false,
      domainForm: {
        domains: '',
        name: '',
        price: 0,
        isFree: false,
        maxPurchases: 1
      },
      domainPreviews: [],
      domainPreviewTimer: null
    }
  },
  computed: {
    allLinkIds() {
      return this.apiLinks.map(l => l.id)
    },
    isAllSelected() {
      return this.apiLinks.length > 0 && this.allLinkIds.every(id => this.selectedIds.has(id))
    },
    groupedApiLinks() {
      const groupMap = {}
      const grouped = new Set()

      for (const link of this.apiLinks) {
        const match = link.name && link.name.match(/^(.+?)(\d+)$/)
        if (match) {
          const key = match[1] + '|||' + (link.price || 0) + '|||' + (link.isFree ? 'free' : 'paid')
          if (!groupMap[key]) groupMap[key] = []
          groupMap[key].push(link)
        }
      }

      for (const links of Object.values(groupMap)) {
        if (links.length >= 2) {
          links.forEach(l => grouped.add(l.id))
        }
      }

      const result = []
      const addedGroups = new Set()
      for (const link of this.apiLinks) {
        if (grouped.has(link.id)) {
          const match = link.name.match(/^(.+?)(\d+)$/)
          const key = match[1] + '|||' + (link.price || 0) + '|||' + (link.isFree ? 'free' : 'paid')
          if (!addedGroups.has(key)) {
            addedGroups.add(key)
            result.push({
              type: 'group',
              id: 'group_' + key,
              prefix: match[1],
              price: link.price,
              isFree: link.isFree,
              count: groupMap[key].length,
              links: groupMap[key],
              createdAt: groupMap[key][0].createdAt,
              createdBy: groupMap[key][0].createdBy
            })
          }
        } else {
          result.push({ type: 'single', link })
        }
      }

      return result
    }
  },
  mounted() {
    this.fetchMyPermissions()
    this.fetchLinks()
  },
  methods: {
    // 选择相关
    toggleSelect(id) {
      const newSet = new Set(this.selectedIds)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      this.selectedIds = newSet
    },
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedIds = new Set()
      } else {
        this.selectedIds = new Set(this.allLinkIds)
      }
    },
    toggleSelectGroup(group) {
      const groupIds = group.links.map(l => l.id)
      const allSelected = groupIds.every(id => this.selectedIds.has(id))
      const newSet = new Set(this.selectedIds)
      if (allSelected) {
        groupIds.forEach(id => newSet.delete(id))
      } else {
        groupIds.forEach(id => newSet.add(id))
      }
      this.selectedIds = newSet
    },
    isGroupSelected(group) {
      return group.links.length > 0 && group.links.every(l => this.selectedIds.has(l.id))
    },
    clearSelection() {
      this.selectedIds = new Set()
    },

    // 批量操作
    async batchDelete() {
      const count = this.selectedIds.size
      if (!confirm(`确定删除选中的 ${count} 个接口吗？`)) return
      try {
        const res = await apiFetch('/api/admin/api-links/batch-delete', {
          method: 'POST',
          body: JSON.stringify({ ids: Array.from(this.selectedIds) })
        })
        if (res.code === 0) {
          const data = res.data
          let msg = `成功删除 ${data.deletedCount} 个接口`
          if (data.errors && data.errors.length > 0) {
            msg += '\n\n以下接口删除失败：\n' + data.errors.join('\n')
          }
          alert(msg)
          this.clearSelection()
          this.fetchLinks()
        } else {
          alert(res.msg || '批量删除失败')
        }
      } catch (e) {
        alert('批量删除失败')
      }
    },
    async batchRefund() {
      const count = this.selectedIds.size
      if (!confirm(`确定对选中的 ${count} 个接口执行退款吗？\n将退还所有购买用户的金额并删除购买记录`)) return
      try {
        const res = await apiFetch('/api/admin/api-links/batch-refund', {
          method: 'POST',
          body: JSON.stringify({ ids: Array.from(this.selectedIds) })
        })
        if (res.code === 0) {
          const data = res.data
          let msg = `退款完成！\n退款人次: ${data.refundedCount}\n退款总额: ¥${data.totalRefunded}`
          if (data.refundedUsers && data.refundedUsers.length > 0) {
            msg += `\n退款用户: ${data.refundedUsers.join(', ')}`
          }
          if (data.errors && data.errors.length > 0) {
            msg += '\n\n以下接口退款失败：\n' + data.errors.join('\n')
          }
          alert(msg)
          this.clearSelection()
          this.fetchLinks()
        } else {
          alert(res.msg || '批量退款失败')
        }
      } catch (e) {
        alert('批量退款失败')
      }
    },

    async fetchMyPermissions() {
      try {
        const res = await apiFetch('/api/admin/users/my-permissions')
        if (res.code === 0) {
          this.isAdmin = res.data.isAdmin
        }
      } catch (e) {
        console.error('获取权限失败', e)
      }
    },
    async fetchLinks() {
      try {
        const res = await apiFetch('/api/admin/api-links')
        if (res.code === 0) {
          this.apiLinks = res.data
          // 清除已不存在的选中项
          if (this.selectedIds.size > 0) {
            const existingIds = new Set(res.data.map(l => l.id))
            const newSet = new Set()
            this.selectedIds.forEach(id => {
              if (existingIds.has(id)) newSet.add(id)
            })
            this.selectedIds = newSet
          }
        }
      } catch (e) {
        console.error('获取接口列表失败', e)
      }
    },

    // 添加
    openAddModal() {
      this.addForm = { url: '', name: '', price: 0, isFree: false, maxPurchases: 1 }
      this.showAddModal = true
    },
    closeAddModal() {
      this.showAddModal = false
    },
    async addLink() {
      if (!this.addForm.url.trim()) {
        alert('请输入接口链接')
        return
      }
      if (!this.addForm.isFree && (!this.addForm.price || this.addForm.price <= 0)) {
        alert('付费接口价格必须大于0')
        return
      }
      try {
        const res = await apiFetch('/api/admin/api-links', {
          method: 'POST',
          body: JSON.stringify(this.addForm)
        })
        if (res.code === 0) {
          this.closeAddModal()
          this.fetchLinks()
        } else {
          alert(res.msg || '添加失败')
        }
      } catch (e) {
        alert('添加失败')
      }
    },

    // 编辑
    startEdit(link) {
      this.editingId = link.id
      this.editForm = {
        url: link.url,
        name: link.name || '',
        price: link.price || 0,
        isFree: link.isFree || false,
        maxPurchases: link.maxPurchases != null ? link.maxPurchases : 1
      }
    },
    cancelEdit() {
      this.editingId = null
    },
    async saveEdit(id) {
      if (!this.editForm.url.trim()) {
        alert('接口链接不能为空')
        return
      }
      try {
        const res = await apiFetch(`/api/admin/api-links/${id}`, {
          method: 'PUT',
          body: JSON.stringify(this.editForm)
        })
        if (res.code === 0) {
          this.cancelEdit()
          this.fetchLinks()
        } else {
          alert(res.msg || '保存失败')
        }
      } catch (e) {
        alert('保存失败')
      }
    },

    // 删除
    async deleteLink(id) {
      if (!confirm('确定删除该接口吗？')) return
      try {
        const res = await apiFetch(`/api/admin/api-links/${id}`, {
          method: 'DELETE'
        })
        if (res.code === 0) {
          this.fetchLinks()
        } else {
          alert(res.msg || '删除失败')
        }
      } catch (e) {
        alert('删除失败')
      }
    },

    copyUrl(url) {
      navigator.clipboard.writeText(url).then(() => {
        alert('已复制到剪贴板')
      })
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleString()
    },

    toggleGroup(groupId) {
      this.expandedGroups = { ...this.expandedGroups, [groupId]: !this.expandedGroups[groupId] }
    },

    async deleteGroup(links) {
      if (!confirm(`确定删除这 ${links.length} 个接口吗？`)) return
      try {
        for (const link of links) {
          await apiFetch(`/api/admin/api-links/${link.id}`, { method: 'DELETE' })
        }
        this.fetchLinks()
      } catch (e) {
        alert('删除失败')
      }
    },

    // 批量导入
    openBatchModal() {
      this.batchForm = { urls: '', name: '', price: 0, isFree: false, maxPurchases: 1 }
      this.showBatchModal = true
    },
    closeBatchModal() {
      this.showBatchModal = false
    },
    async batchImport() {
      if (!this.batchForm.urls.trim()) {
        alert('请输入接口链接')
        return
      }
      if (!this.batchForm.isFree && (!this.batchForm.price || this.batchForm.price <= 0)) {
        alert('付费接口价格必须大于0')
        return
      }
      try {
        const res = await apiFetch('/api/admin/api-links/batch', {
          method: 'POST',
          body: JSON.stringify(this.batchForm)
        })
        if (res.code === 0) {
          alert('成功导入 ' + (res.data ? res.data.length : 0) + ' 个接口')
          this.closeBatchModal()
          this.fetchLinks()
        } else {
          alert(res.msg || '导入失败')
        }
      } catch (e) {
        alert('导入失败')
      }
    },

    // 导入域名
    openDomainModal() {
      this.domainForm = { domains: '', name: '', price: 0, isFree: false, maxPurchases: 1 }
      this.domainPreviews = []
      this.showDomainModal = true
    },
    closeDomainModal() {
      this.showDomainModal = false
      this.domainPreviews = []
      if (this.domainPreviewTimer) {
        clearTimeout(this.domainPreviewTimer)
        this.domainPreviewTimer = null
      }
    },
    generateDomainPreview() {
      // 防抖：输入后 300ms 再生成预览
      if (this.domainPreviewTimer) clearTimeout(this.domainPreviewTimer)
      this.domainPreviewTimer = setTimeout(() => {
        this._doGeneratePreview()
      }, 300)
    },
    async _doGeneratePreview() {
      const text = this.domainForm.domains.trim()
      if (!text) {
        this.domainPreviews = []
        return
      }
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l)
      const namePrefix = this.domainForm.name || '域名'

      const previews = []
      for (let i = 0; i < lines.length; i++) {
        let domain = lines[i]
        // 去掉协议前缀（用户可能粘贴了完整URL）
        domain = domain.replace(/^https?:\/\//, '').replace(/\/+$/, '')
        // 直接拼接为 https://域名
        const fullUrl = `https://${domain}`
        previews.push({
          domain,
          name: namePrefix + (i + 1),
          url: fullUrl,
          qrDataUrl: null
        })
      }
      this.domainPreviews = previews

      // 异步生成二维码
      for (let i = 0; i < this.domainPreviews.length; i++) {
        try {
          const dataUrl = await QRCode.toDataURL(this.domainPreviews[i].url, {
            width: 128,
            margin: 1,
            errorCorrectionLevel: 'H',
            color: { dark: '#000000', light: '#ffffff' }
          })
          this.domainPreviews[i].qrDataUrl = dataUrl
        } catch (e) {
          console.error('生成二维码失败:', e)
        }
      }
      // 触发响应式更新
      this.domainPreviews = [...this.domainPreviews]
    },
    async domainImport() {
      if (this.domainPreviews.length === 0) {
        alert('请输入域名')
        return
      }
      if (!this.domainForm.isFree && (!this.domainForm.price || this.domainForm.price <= 0)) {
        alert('付费接口价格必须大于0')
        return
      }
      // 把预览中的完整URL组装成批量导入格式
      const urls = this.domainPreviews.map(p => p.url).join('\n')
      try {
        const res = await apiFetch('/api/admin/api-links/batch', {
          method: 'POST',
          body: JSON.stringify({
            urls,
            name: this.domainForm.name || '域名',
            price: this.domainForm.price,
            isFree: this.domainForm.isFree,
            maxPurchases: this.domainForm.maxPurchases
          })
        })
        if (res.code === 0) {
          alert('成功导入 ' + (res.data ? res.data.length : 0) + ' 个接口')
          this.closeDomainModal()
          this.fetchLinks()
        } else {
          alert(res.msg || '导入失败')
        }
      } catch (e) {
        alert('导入失败')
      }
    },

    // 退款相关
    async openRefundModal(link) {
      this.refundTarget = link
      this.showRefundModal = true
      this.isLoadingRefund = true
      this.refundPurchases = []
      try {
        const res = await apiFetch(`/api/admin/api-links/${link.id}/purchases`)
        if (res.code === 0) {
          this.refundPurchases = res.data || []
        }
      } catch (e) {
        console.error('获取购买记录失败', e)
      } finally {
        this.isLoadingRefund = false
      }
    },
    closeRefundModal() {
      this.showRefundModal = false
      this.refundTarget = null
      this.refundPurchases = []
    },
    async confirmRefund() {
      if (!this.refundTarget) return
      const count = this.refundPurchases.length
      if (count === 0) {
        alert('该接口暂无购买记录')
        return
      }
      if (!confirm(`确定退款？将退还 ${count} 位用户的购买金额并删除购买记录`)) return
      try {
        const res = await apiFetch(`/api/admin/api-links/${this.refundTarget.id}/refund-all`, {
          method: 'POST'
        })
        if (res.code === 0) {
          const data = res.data
          alert(`退款成功！\n退款人数: ${data.refundedCount}\n退款总额: ¥${data.totalRefunded}\n退款用户: ${data.refundedUsers.join(', ')}`)
          this.closeRefundModal()
          this.fetchLinks()
        } else {
          alert(res.msg || '退款失败')
        }
      } catch (e) {
        alert('退款失败')
      }
    },
    formatRefundDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleString()
    }
  }
}
</script>

<style scoped>
.interface-management { height: 100%; display: flex; flex-direction: column; min-height: 0; overflow-y: auto; -webkit-overflow-scrolling: touch; }
.toolbar { display: flex; justify-content: space-between; align-items: center; padding: 14px 0; margin-bottom: 16px; }
.toolbar-left { display: flex; align-items: center; gap: 12px; }
.toolbar-left h3 { margin: 0; font-size: 15px; font-weight: 600; color: #111; }
.stats { font-size: 13px; color: #999; }
.row-checkbox { width: 16px; height: 16px; cursor: pointer; accent-color: #111; }
.batch-bar { display: flex; justify-content: space-between; align-items: center; padding: 10px 16px; margin-bottom: 12px; background: #f8f8f8; border: 1px solid #e0e0e0; border-radius: 4px; }
.batch-info { display: flex; align-items: center; gap: 12px; font-size: 13px; color: #333; }
.batch-count strong { color: #111; }
.batch-link { border: none; background: none; color: #999; font-size: 12px; cursor: pointer; text-decoration: underline; padding: 0; }
.batch-link:hover { color: #333; }
.batch-actions { display: flex; gap: 8px; }
.btn-batch-refund { display: inline-flex; align-items: center; gap: 6px; padding: 6px 14px; border: 1px solid #ffcdd2; border-radius: 4px; font-size: 13px; font-weight: 500; cursor: pointer; background: #fff; color: #d32f2f; }
.btn-batch-refund:hover { background: #d32f2f; color: #fff; border-color: #d32f2f; }
.btn-batch-delete { display: inline-flex; align-items: center; gap: 6px; padding: 6px 14px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px; font-weight: 500; cursor: pointer; background: #fff; color: #666; }
.btn-batch-delete:hover { background: #111; color: #fff; border-color: #111; }
.card-selected { border-color: #111 !important; background: #fafafa; }
.btn { display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border: none; border-radius: 4px; font-size: 13px; font-weight: 500; cursor: pointer; }
.btn-primary { background: #111; color: #fff; }
.btn-primary:hover { background: #333; }
.btn-secondary { background: #fff; color: #333; border: 1px solid #ddd; }
.btn-secondary:hover { background: #f5f5f5; }
.table-container { flex: 1; background: #fff; overflow: visible; border: 1px solid #e8e8e8; }
.data-table { width: 100%; border-collapse: collapse; }
.data-table th { padding: 10px 14px; text-align: left; font-size: 12px; font-weight: 600; color: #666; background: #fafafa; border-bottom: 1px solid #e8e8e8; text-transform: uppercase; letter-spacing: 0.3px; }
.data-table td { padding: 10px 14px; font-size: 13px; color: #333; border-bottom: 1px solid #f0f0f0; }
.data-table tr:hover { background: #fafafa; }
.text-center { text-align: center; }
.text-muted { color: #999; }
.url-cell { display: flex; align-items: center; gap: 8px; }
.url-text { flex: 1; word-break: break-all; font-family: monospace; font-size: 12px; color: #666; }
.icon-btn { padding: 4px; border: none; background: none; color: #999; cursor: pointer; border-radius: 3px; }
.icon-btn:hover { background: #f5f5f5; color: #333; }
.price-text { color: #111; font-weight: 600; }
.limit-tag { display: inline-block; padding: 1px 6px; margin-left: 4px; border-radius: 3px; font-size: 10px; font-weight: 500; background: #f5f5f5; color: #999; }
.limit-tag.unlimited { background: #e3f2fd; color: #1565c0; }
.creator-tag { display: inline-block; padding: 2px 8px; background: #f5f5f5; color: #666; border-radius: 3px; font-size: 12px; }
.edit-input { width: 100%; padding: 6px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px; }
.edit-input:focus { outline: none; border-color: #111; }
.action-group { display: flex; gap: 4px; }
.action-btn { display: inline-flex; align-items: center; gap: 4px; padding: 4px 8px; border: 1px solid #ddd; border-radius: 3px; font-size: 12px; cursor: pointer; background: #fff; color: #666; }
.action-btn.edit:hover { background: #111; color: #fff; border-color: #111; }
.action-btn.delete:hover { background: #111; color: #fff; border-color: #111; }
.action-btn.save { color: #111; border-color: #111; }
.action-btn.save:hover { background: #111; color: #fff; }
.action-btn.cancel:hover { background: #f5f5f5; }
.group-row { cursor: pointer; background: #fafafa; }
.group-row:hover { background: #f0f0f0; }
.group-name-cell { display: flex; align-items: center; gap: 8px; }
.group-arrow { color: #999; font-size: 12px; width: 14px; display: inline-block; }
.group-badge { display: inline-block; padding: 1px 6px; background: #111; color: #fff; border-radius: 3px; font-size: 11px; font-weight: 500; }
.group-child-row { background: #fafbfc; }
.group-child-row td:first-child { padding-left: 28px; }
.free-tag { display: inline-block; padding: 2px 8px; background: #e8f5e9; color: #2e7d32; border-radius: 3px; font-size: 12px; font-weight: 600; }
.type-toggle { display: flex; gap: 0; border: 1px solid #ddd; border-radius: 4px; overflow: hidden; }
.type-btn { flex: 1; padding: 8px 16px; border: none; background: #fff; font-size: 13px; font-weight: 500; cursor: pointer; color: #666; transition: all 0.2s; }
.type-btn.active { background: #111; color: #fff; }
.type-btn.free.active { background: #2e7d32; color: #fff; }
.type-btn.unlimited.active { background: #1565c0; color: #fff; }
.empty-state { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #fff; padding: 60px 20px; border: 1px solid #e8e8e8; }
.empty-icon { color: #ddd; margin-bottom: 16px; }
.empty-state p { margin: 0 0 16px; font-size: 14px; color: #999; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-box { background: #fff; width: 480px; max-width: 90vw; border: 1px solid #e8e8e8; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #f0f0f0; }
.modal-header h4 { margin: 0; font-size: 15px; font-weight: 600; color: #111; }
.modal-close { padding: 4px; border: none; background: none; color: #999; cursor: pointer; border-radius: 3px; }
.modal-close:hover { background: #f5f5f5; color: #333; }
.modal-body { padding: 20px; }
.form-item { margin-bottom: 16px; }
.form-item:last-child { margin-bottom: 0; }
.form-item label { display: block; margin-bottom: 6px; font-size: 13px; font-weight: 500; color: #333; }
.required { color: #999; }
.form-input { width: 100%; padding: 9px 12px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px; box-sizing: border-box; }
.form-input:focus { outline: none; border-color: #111; }
.modal-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 16px 20px; border-top: 1px solid #f0f0f0; }
.btn-refund { display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border: none; border-radius: 4px; font-size: 13px; font-weight: 500; cursor: pointer; background: #d32f2f; color: #fff; }
.btn-refund:hover { background: #b71c1c; }
.btn-refund:disabled { background: #ccc; cursor: not-allowed; }
.action-btn.refund { color: #d32f2f; border-color: #ffcdd2; }
.action-btn.refund:hover { background: #d32f2f; color: #fff; border-color: #d32f2f; }
.refund-user-list { max-height: 200px; overflow-y: auto; border: 1px solid #e8e8e8; border-radius: 4px; }
.refund-user-item { display: flex; align-items: center; gap: 12px; padding: 8px 12px; border-bottom: 1px solid #f0f0f0; font-size: 13px; }
.refund-user-item:last-child { border-bottom: none; }
.refund-username { font-weight: 500; color: #111; flex: 1; }
.refund-price { color: #d32f2f; font-weight: 600; }
.refund-date { color: #999; font-size: 12px; }
.card-list { display: none; }
.btn-domain { background: #1565c0; color: #fff; border: none; }
.btn-domain:hover { background: #0d47a1; }
.domain-modal-box { width: 580px; max-height: 90vh; overflow-y: auto; }
.form-row { display: flex; gap: 12px; }
.domain-preview-list { max-height: 260px; overflow-y: auto; border: 1px solid #e8e8e8; border-radius: 4px; }
.domain-preview-item { display: flex; align-items: center; gap: 12px; padding: 10px 12px; border-bottom: 1px solid #f0f0f0; }
.domain-preview-item:last-child { border-bottom: none; }
.domain-preview-info { flex: 1; min-width: 0; }
.domain-preview-name { font-size: 13px; font-weight: 600; color: #111; margin-bottom: 4px; }
.domain-preview-url { font-size: 11px; color: #666; font-family: monospace; word-break: break-all; }
.domain-preview-qr { flex-shrink: 0; }
.domain-preview-qr img { display: block; border: 1px solid #eee; border-radius: 4px; }
.qr-loading { width: 64px; height: 64px; display: flex; align-items: center; justify-content: center; font-size: 11px; color: #999; background: #fafafa; border: 1px solid #eee; border-radius: 4px; }
@media screen and (max-width: 768px) {
  .card-list { display: flex; flex-direction: column; gap: 12px; flex: 1; overflow-y: visible; min-height: 0; padding-bottom: 20px; }
  .api-card { background: #fff; overflow: hidden; border: 1px solid #e8e8e8; }
  .card-header { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; background: #fafafa; border-bottom: 1px solid #f0f0f0; }
  .card-index { font-size: 13px; font-weight: 600; color: #666; }
  .card-price { font-size: 15px; font-weight: 600; color: #111; }
  .card-body { padding: 12px 14px; }
  .card-name { font-size: 14px; font-weight: 600; color: #111; margin-bottom: 8px; }
  .card-url { font-size: 12px; color: #666; word-break: break-all; font-family: monospace; background: #fafafa; padding: 8px 10px; margin-bottom: 8px; }
  .card-time { font-size: 12px; color: #999; }
  .card-meta { display: flex; justify-content: space-between; align-items: center; margin-top: 4px; }
  .card-meta .creator-tag { font-size: 11px; padding: 2px 6px; }
  .card-edit-form { display: flex; flex-direction: column; gap: 10px; }
  .card-input { width: 100%; padding: 10px 12px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px; box-sizing: border-box; }
  .card-input:focus { outline: none; border-color: #111; }
  .card-actions { display: flex; border-top: 1px solid #f0f0f0; }
  .card-btn { flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 11px; border: none; background: none; font-size: 13px; font-weight: 500; cursor: pointer; color: #333; }
  .card-btn:not(:last-child) { border-right: 1px solid #f0f0f0; }
  .card-btn:active { background: #f5f5f5; }
  .card-btn.copy { color: #333; }
  .card-btn.edit { color: #333; }
  .card-btn.delete { color: #999; }
  .card-btn.refund { color: #d32f2f; }
  .card-btn.save { color: #111; font-weight: 600; }
  .card-btn.cancel { color: #999; }
  .group-card { border-left: 3px solid #111; }
  .group-card .group-arrow { font-size: 14px; margin-right: 4px; }
  .group-card .group-badge { font-size: 11px; margin-left: 4px; }
  .group-child-card { border-top: 1px solid #f0f0f0; }
  .toolbar { flex-direction: column; gap: 12px; padding: 12px 0; }
  .toolbar-left { width: 100%; justify-content: space-between; }
  .toolbar-right { width: 100%; }
  .toolbar-right .btn { width: 100%; justify-content: center; padding: 10px 16px; }
  .table-container { display: none; }
  .batch-bar { flex-direction: column; gap: 10px; padding: 12px; }
  .batch-info { width: 100%; justify-content: space-between; }
  .batch-actions { width: 100%; }
  .batch-actions .btn-batch-refund, .batch-actions .btn-batch-delete { flex: 1; justify-content: center; padding: 10px; }
  .modal-box { width: 95%; margin: 10px; max-height: 90vh; overflow-y: auto; }
  .domain-modal-box { width: 95%; }
  .form-row { flex-direction: column; gap: 0; }
  .modal-footer { flex-direction: column; padding: 12px 16px; }
  .modal-footer .btn { width: 100%; padding: 11px; }
  .empty-state { padding: 40px 20px; }
}
</style>
